import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { handleGoogleLogin, formatApiErrorDetail } from "@/lib/googleLogin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LoadingScreen from "@/components/LoadingScreen";
import { ArrowLeft, UserPlus, Mail, Loader2, AlertCircle, KeyRound } from "lucide-react";

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [loginPassword, setLoginPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then((r) => {
        setInvite(r.data);
        setForm((f) => ({ ...f, name: r.data.name || "" }));
      })
      .catch((e) => setLoadError(formatApiErrorDetail(e?.response?.data?.detail)));
  }, [token]);

  const needsPassword = invite && !invite.has_password && !invite.used;

  const acceptAndSetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) return setError("La contraseña debe tener al menos 6 caracteres.");
    if (form.password !== form.confirm) return setError("Las contraseñas no coinciden.");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invitations/${token}/accept`, {
        name: form.name.trim() || null,
        password: form.password,
      });
      setUser(data);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const loginExisting = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email: invite.email, password: loginPassword });
      setUser(data);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="invitation-error">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center">
          <div className="h-12 w-12 rounded-xl bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-semibold mb-2">Invitación no disponible</h1>
          <p className="text-sm text-muted-foreground mb-6">{loadError}</p>
          <Button asChild className="rounded-full"><Link to="/">Ir al inicio</Link></Button>
        </div>
      </div>
    );
  }

  if (!invite) return <LoadingScreen text="Abriendo tu invitación…" />;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="invitation-page">
      <header className="border-b border-border/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/mori-logo.jpg" alt="Mori" className="h-9 w-9 rounded-lg object-cover" />
            <span className="text-xl font-bold tracking-tight lowercase">mori</span>
          </Link>
          <Button variant="ghost" className="rounded-full gap-2 text-muted-foreground" onClick={() => navigate("/")}>
            <ArrowLeft className="h-4 w-4" /> Inicio
          </Button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12 relative overflow-hidden">
        <div className="absolute inset-x-0 bottom-0 h-40 seigaiha-pattern opacity-60 pointer-events-none" />
        <div className="w-full max-w-md animate-fade-up relative">
          <div className="rounded-2xl border border-[#C75B28]/15 bg-card p-8 shadow-[0_10px_40px_rgba(165,70,30,0.14)]">
            <div className="h-11 w-11 rounded-xl bg-[#C75B28]/10 text-[#C75B28] flex items-center justify-center mb-5">
              {needsPassword ? <UserPlus className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}
            </div>
            <h1 className="text-2xl font-bold tracking-tight">
              {needsPassword ? "Configura tu acceso" : "Inicia sesión para entrar"}
            </h1>
            <p className="text-sm text-muted-foreground mt-2 mb-1">
              <strong>{invite.invited_by || "Un administrador"}</strong> te ha invitado al proyecto{" "}
              <strong>«{invite.project_name}»</strong> como <strong>{invite.role_label}</strong>.
            </p>
            <p className="text-xs text-muted-foreground font-mono mb-6">{invite.email}</p>

            {needsPassword ? (
              <form onSubmit={acceptAndSetPassword} className="space-y-4" data-testid="set-password-form">
                <div className="space-y-1.5">
                  <Label htmlFor="inv-name">Tu nombre</Label>
                  <Input id="inv-name" data-testid="invite-name-input" placeholder="Ej. Lucía Gómez"
                    value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="inv-pass">Crea una contraseña</Label>
                  <Input id="inv-pass" type="password" required data-testid="invite-password-input"
                    placeholder="Mínimo 6 caracteres" value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="inv-confirm">Repite la contraseña</Label>
                  <Input id="inv-confirm" type="password" required data-testid="invite-confirm-input"
                    placeholder="Repite tu contraseña" value={form.confirm}
                    onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="invite-error">{error}</p>}
                <Button type="submit" className="w-full rounded-full h-11 !bg-[#C75B28] hover:!bg-[#15213a] !text-[#F8F5EE]" disabled={submitting} data-testid="invite-submit-button">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear acceso y entrar"}
                </Button>
              </form>
            ) : (
              <form onSubmit={loginExisting} className="space-y-4" data-testid="invite-login-form">
                <p className="text-sm text-muted-foreground">Ya tienes una cuenta con este email. Introduce tu contraseña para continuar.</p>
                <div className="space-y-1.5">
                  <Label htmlFor="inv-login-pass">Contraseña</Label>
                  <Input id="inv-login-pass" type="password" required data-testid="invite-login-password-input"
                    placeholder="Tu contraseña" value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)} />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="invite-error">{error}</p>}
                <Button type="submit" className="w-full rounded-full h-11 !bg-[#C75B28] hover:!bg-[#15213a] !text-[#F8F5EE]" disabled={submitting} data-testid="invite-login-submit-button">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
                </Button>
              </form>
            )}

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center"><span className="bg-card px-3 text-xs text-muted-foreground">o</span></div>
            </div>

            <Button variant="outline" className="w-full rounded-full h-11 gap-2" data-testid="invite-google-button"
              onClick={() => handleGoogleLogin("/")}>
              <Mail className="h-4 w-4" /> Entrar con Google ({invite.email})
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-4">
              Si usas Google, entra con la misma dirección a la que te hemos invitado.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
